import { BLOCKS, Document } from '@contentful/rich-text-types'
import { documentToReactComponents, RenderNode } from '@contentful/rich-text-react-renderer'
import styled from '@emotion/styled'
import { COLORS } from 'parameters/index'
import { replaceShortcode } from 'components/vendor/Contentful/richText/ShortcodeManager'
import { idFromNode } from 'services/ContentfulService/models/resource/utils'
import React from 'react'
import { Stack } from 'components/layout/primitives/Stack'

interface Props {
	document: Document
}

const nodeRenderers: RenderNode = {
	[BLOCKS.EMBEDDED_ENTRY]: node => {
		const nodeTarget = node.data.target
		const contentType = nodeTarget.sys.contentType.sys.id as string
		const fields = nodeTarget.fields
		switch (contentType) {
			case 'resource':
				return <div dangerouslySetInnerHTML={{ __html: fields.markdownValue }} />
			case undefined:
			default:
				return null
		}
	},

	[BLOCKS.HEADING_2]: (node, children) => {
		return <h2 id={idFromNode(node)}>{children}</h2>
	},
	[BLOCKS.PARAGRAPH]: (node, children) => {
		if (Array.isArray(children)) {
			try {
				// Handles Embeds, which should not be in a <p> tag
				const widget = children.find(
					element => typeof element === 'object' && element.type?.name === 'MultiProductWidget'
				)
				if (widget) {
					return widget
				}
			} catch (e) {
				console.log(e)
			}
			// Handles multiple nodes to support very old articles using a legacy format
			if (children.length > 1) return <p>{children}</p>
			// Handles standalone new line elements, which should not be rendered
			if (children[0] === '\n') return <></>
			if (typeof children[0] === 'object' && !Array.isArray(children[0])) {
				// Handles custom block Button elements, which only appear alone, and are annoying to try to identify explicitly, and cannot be rendered in a <p>
				return <>{children}</>
			}
		}
		// Handles Inline text node groups, including links, which must be in a <p> tag
		return <p>{children}</p>
	}
}

export const BasicRichText: React.FC<React.PropsWithChildren<Props>> = ({ document }) => {
	const result = documentToReactComponents(document, { renderText: replaceShortcode, renderNode: nodeRenderers })
	return <>{result}</>
}

export const RichText: React.FC<React.PropsWithChildren<Props>> = ({ document }) => {
	const result = documentToReactComponents(document, { renderText: replaceShortcode, renderNode: nodeRenderers })
	return <StyleWrapper>{result}</StyleWrapper>
}

const StyleWrapper = styled(Stack)`
	// TODO: Should be replaced by custom UL, OL
	ul,
	ol {
		padding-left: 1em;
	}

	// TODO: Should be replaced by custom anchor component
	a {
		color: ${COLORS.DARK_ORCHID_PURPLE};
	}

	// TODO: Should be replaced by horizontal divider component
	hr {
		border: 0;
		border-bottom: 1px solid #e2e5e9;
		margin: 2rem 0;
	}
`
